import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { Stack } from '@mui/system';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { RHFAutoComplete } from '@openx/components-core/lib/AutoComplete/RHFAutoComplete.tsx';
import { RHFDatePickerField } from '@openx/components-core/lib/DatePickerField/RHFDatePickerField.tsx';
import { RHFCurrencyField } from '@openx/components-core/lib/NumberField/RHFCurrencyField.tsx';
import { RHFTextField } from '@openx/components-core/lib/TextField/RHFTextField.tsx';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getDealTypeLabels, getFormLabels, getFormPlaceholders } from './form/const.ts';
import { type RequestDirectDealForm, requestDirectDealFields } from './form/types.ts';
import { getDateRanges } from './form/utils.ts';

type ModalContentProps = {
  publisherName: string;
};

export const ModalContent = ({ publisherName }: ModalContentProps) => {
  const { t } = useTranslation();

  const formLabels = useMemo(() => getFormLabels(t), [t]);
  const formPlaceholders = useMemo(() => getFormPlaceholders(t), [t]);
  const dealTypeLabels = useMemo(() => getDealTypeLabels(t), [t]);

  const observableStartDate = useWatch<RequestDirectDealForm>({ name: requestDirectDealFields.START_DATE });
  const { minStartDate, minEndDate } = useMemo(() => getDateRanges(observableStartDate), [observableStartDate]);

  return (
    <DialogContent data-test="request-direct-deal-modal-content">
      <Stack
        sx={{
          mb: 2,
        }}
      >
        <Typography>{t('You are requesting a Direct Deal to:')}</Typography>
        <Typography variant="body1Bold" data-test="request-direct-deal-publisher-name">
          {publisherName}
        </Typography>
      </Stack>
      <Typography>{t('Please provide some initial Deal Details. They can be redefined later.')}</Typography>
      <Stack
        spacing={1}
        sx={{
          width: '100%',
        }}
      >
        <RHFAutoComplete
          data-test="deal-type"
          disableClearable
          name={requestDirectDealFields.DEAL_TYPE}
          options={Object.values(dealTypeLabels)}
          textFieldProps={{
            label: formLabels.dealType,
            margin: 'dense',
            placeholder: formPlaceholders.dealType,
          }}
        />

        <RHFTextField
          dataTest="advertiser-brand"
          name={requestDirectDealFields.ADVERTISER_BRAND}
          label={formLabels.advertiserBrand}
          placeholder={formPlaceholders.advertiserBrand}
        />

        <RHFCurrencyField
          dataTest="budget"
          name={requestDirectDealFields.BUDGET}
          label={formLabels.budget}
          placeholder={formPlaceholders.budget}
        />

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack direction="row" spacing={1}>
            <RHFDatePickerField
              data-test="start-date"
              name={requestDirectDealFields.START_DATE}
              label={formLabels.startDate}
              minDate={minStartDate}
              clearable
            />

            <RHFDatePickerField
              data-test="end-date"
              name={requestDirectDealFields.END_DATE}
              label={formLabels.endDate}
              minDate={minEndDate}
              clearable
            />
          </Stack>
        </LocalizationProvider>

        <RHFTextField
          dataTest="targeting-details"
          name={requestDirectDealFields.TARGETING_DETAILS}
          label={formLabels.targetingDetails}
          placeholder={formPlaceholders.targetingDetails}
          multiline
          rows={2}
        />
      </Stack>
    </DialogContent>
  );
};
